import {
	Bar,
	BarChart,
	CartesianGrid,
	LabelList,
	Legend,
	ResponsiveContainer,
	Tooltip,
	TooltipProps,
	XAxis,
	YAxis,
} from 'recharts';
import { FC, ReactNode, memo, useMemo } from 'react';

import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { CHART_SERIES_COLORS, chartContrastStroke } from 'features/common/utils/chartColors';
import { computeYAxisWidth } from 'features/common/utils/chartAxis';
import { common } from 'theme';
import styles from './PunchupStackedBarChart.module.css';

/**
 * Cap on bar thickness (px). Bars shrink below this to fit when there are many categories, but a lone
 * bar never stretches to a full-width slab — the BI-standard alternative to fabricating empty buckets.
 */
const DEFAULT_MAX_BAR_SIZE = 56;

export interface BarSeries {
	/** Key in each data entry to render as one bar layer. */
	dataKey: string;
	/** Fill color for this series. Use CHART_SERIES_COLORS[i] for consistent palette. */
	color: string;
	/** Human-readable series name shown in the legend and tooltip. */
	name: string;
}

export interface PunchupStackedBarChartProps {
	/** Pre-processed data array. Each entry must include the x-axis key and all series keys. */
	data: Record<string, unknown>[];
	/**
	 * Bar series definitions in render order (bottom → top when stacked).
	 * Use CHART_SERIES_COLORS[i] for colors to stay in token-aligned palette.
	 */
	bars: BarSeries[];
	/** Key in each data entry for X axis labels. Defaults to 'date'. */
	xAxisKey?: string;
	/** Card header title (e.g. "Tickets Sold"). */
	title?: string;
	/** Icon rendered left of the title. Accepts any ReactNode sized to ~18px. */
	icon?: ReactNode;
	/**
	 * Aggregate label shown below the title (e.g. "Total Tickets Sold").
	 * Displayed as muted secondary text.
	 */
	totalLabel?: string;
	/**
	 * Aggregate value shown next to totalLabel (e.g. "1,284").
	 * Displayed at display scale (26px/700).
	 */
	totalValue?: string | number;
	/**
	 * Slot for controls on the right side of the card header.
	 * Intended for TimelineControls (period/zoom selectors).
	 */
	headerRight?: ReactNode;
	/** Formats X-axis tick labels (e.g. abbreviated dates). Receives the raw data value. */
	xAxisFormatter?: (value: unknown) => string;
	/** Formats Y-axis tick labels. */
	yAxisFormatter?: (value: number) => string;
	/**
	 * Custom Recharts Tooltip content component.
	 * When omitted, Recharts renders its default tooltip.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	customTooltip?: FC<TooltipProps<any, any>>;
	/** When true, a Recharts Legend is rendered below the chart. Defaults to true. */
	showLegend?: boolean;
	/** Chart area height in px. Defaults to 260. */
	height?: number;
	/**
	 * When provided, renders a label above each bar with the return value.
	 * Receives the bar's value and the sum of all values for that series across the dataset.
	 * Typical use: show each bar as a percentage of the series total.
	 * Example: `(value, total) => \`${Math.round((value / total) * 100)}%\``
	 */
	barLabelFormatter?: (value: number, seriesTotal: number) => string;
	/** When true, renders a fixed-height loading shell instead of the chart — no layout shift. */
	isLoading?: boolean;
	/**
	 * Optional help icon anchored to the bottom-right corner of the card.
	 * Pass a <PunchupHelpButton> — the card already has position:relative.
	 */
	helpButton?: ReactNode;
	/**
	 * Max characters to display in the legend before truncating with "…".
	 * Full name shown in the native title attribute on hover. Default: 22.
	 */
	legendMaxLength?: number;
	/**
	 * Cap on bar thickness in px (Recharts `maxBarSize`). Bars shrink to fit when there are many
	 * categories, but a single-category chart shows a tidy capped bar instead of a full-width slab.
	 * Default: 56.
	 */
	maxBarSize?: number;
}

const PunchupStackedBarChartBase: FC<PunchupStackedBarChartProps> = ({
	data,
	bars,
	xAxisKey = 'date',
	title,
	icon,
	totalLabel,
	totalValue,
	headerRight,
	xAxisFormatter,
	yAxisFormatter,
	customTooltip,
	showLegend = true,
	height = 260,
	barLabelFormatter,
	isLoading,
	helpButton,
	legendMaxLength = 22,
	maxBarSize = DEFAULT_MAX_BAR_SIZE,
}) => {
	const hasHeader = !!(title || icon || totalLabel || totalValue !== undefined || headerRight);

	// Custom legend so each swatch carries a darker proportional border (matching the bars), instead of
	// Recharts' borderless default icon. Preserves the truncation + full-name title of the old formatter.
	// `any` props: Recharts' legend-content type is internal — the file already types its Recharts
	// callbacks (activeShape, tooltip formatter) the same way.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const renderLegend = ({ payload }: any) => (
		<ul
			style={{
				display: 'flex',
				flexWrap: 'wrap',
				justifyContent: 'center',
				gap: 12,
				margin: 0,
				padding: '8px 0 0',
				listStyle: 'none',
				fontFamily: common.fonts.primary,
				fontSize: 12,
				color: common.colors.secondaryBlue,
			}}
		>
			{payload?.map((entry: { value: string; color: string }) => {
				const label = entry.value.length > legendMaxLength ? `${entry.value.slice(0, legendMaxLength)}…` : entry.value;
				return (
					<li key={entry.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
						<span
							aria-hidden
							style={{
								width: 10,
								height: 10,
								borderRadius: 2,
								flexShrink: 0,
								backgroundColor: entry.color,
								border: `1px solid ${chartContrastStroke(entry.color)}`,
							}}
						/>
						<span title={entry.value}>{label}</span>
					</li>
				);
			})}
		</ul>
	);

	// Compute per-series totals only when barLabelFormatter is provided.
	const seriesTotals = useMemo<Record<string, number>>(() => {
		if (!barLabelFormatter) return {};
		return bars.reduce<Record<string, number>>((acc, bar) => {
			acc[bar.dataKey] = data.reduce((sum, d) => sum + ((d[bar.dataKey] as number) ?? 0), 0);
			return acc;
		}, {});
	}, [bars, data, barLabelFormatter]);

	// Size the Y-axis gutter to the tallest STACK (sum across all series per x),
	// since that is what the top tick reflects — so labels never clip.
	const yAxisWidth = useMemo(() => {
		const stackSums = data.map((d) => bars.reduce((sum, bar) => sum + ((d[bar.dataKey] as number) ?? 0), 0));
		return computeYAxisWidth(stackSums, yAxisFormatter);
	}, [data, bars, yAxisFormatter]);

	return (
		<div className={styles.card}>
			{hasHeader && (
				<div className={styles.cardHeader}>
					<div className={styles.cardHeaderLeft}>
						{(title || icon) && (
							<div className={styles.titleRow}>
								{icon && <span className={styles.iconSlot}>{icon}</span>}
								{title && <span className={styles.title}>{title}</span>}
							</div>
						)}
						{(totalLabel || totalValue !== undefined) && (
							<div className={styles.totalRow}>
								{totalLabel && <span className={styles.totalLabel}>{totalLabel}</span>}
								{totalValue !== undefined && <span className={styles.totalValue}>{totalValue}</span>}
							</div>
						)}
					</div>
					{headerRight && <div className={styles.cardHeaderRight}>{headerRight}</div>}
				</div>
			)}
			{isLoading ? (
				<div className={styles.loadingShell} style={{ height }}>
					<PunchupSpinner size={32} />
				</div>
			) : (
				<div className={styles.chartArea} style={{ height }}>
					<ResponsiveContainer width="100%" height="100%">
						<BarChart
							data={data}
							maxBarSize={maxBarSize}
							margin={{ top: barLabelFormatter ? 20 : 8, right: 16, left: 0, bottom: 0 }}
						>
							<CartesianGrid strokeDasharray="3 3" vertical={false} stroke={common.colors.grey19} />
							<XAxis
								dataKey={xAxisKey}
								tickFormatter={xAxisFormatter}
								tick={{
									fill: common.colors.secondaryBlue,
									fontSize: 12,
									fontFamily: common.fonts.primary,
								}}
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								tickFormatter={yAxisFormatter}
								tick={{
									fill: common.colors.secondaryBlue,
									fontSize: 12,
									fontFamily: common.fonts.primary,
								}}
								tickLine={false}
								axisLine={false}
								width={yAxisWidth}
								allowDecimals={false}
							/>
							<Tooltip content={customTooltip} />
							{showLegend && <Legend content={renderLegend} />}
							{bars.map((bar) => (
								<Bar
									key={bar.dataKey}
									dataKey={bar.dataKey}
									name={bar.name}
									fill={bar.color}
									stroke={chartContrastStroke(bar.color)}
									strokeWidth={1}
									stackId="stack"
									isAnimationActive={false}
								>
									{barLabelFormatter && (
										<LabelList
											dataKey={bar.dataKey}
											position="top"
											formatter={(value: number) => barLabelFormatter(value, seriesTotals[bar.dataKey])}
											style={{
												fontSize: 11,
												fontFamily: common.fonts.primary,
												fill: common.colors.secondaryBlue,
											}}
										/>
									)}
								</Bar>
							))}
						</BarChart>
					</ResponsiveContainer>
				</div>
			)}
			{helpButton}
		</div>
	);
};

export const PunchupStackedBarChart = memo(PunchupStackedBarChartBase);
PunchupStackedBarChart.displayName = 'PunchupStackedBarChart';

export { CHART_SERIES_COLORS };
